/**
 * The Terms and Conditions, the Disclaimer and the Privacy Policy (legal/ in the repository,
 * served by GET /api/v1/legal): the pages that show them, the screen that asks
 * for acceptance the first time someone signs in - and again after a material
 * change - and the footer that keeps both a click away on every page.
 *
 * The documents are Markdown of a simple kind (headings, paragraphs, numbered
 * and bulleted lists, bold, code); `Doc` draws exactly that, as React elements,
 * so nothing in the text is ever treated as HTML.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api, type LegalInfo, type Me } from './api.ts';
import { signOutAndLeave } from './idle.tsx';
import { Link } from './router.tsx';
import { t } from './strings.ts';
import { ContactButton, ContactForm } from './contact.tsx';
import { AuthCard } from './pages/auth.tsx';

let cache: Promise<LegalInfo> | null = null;
/** The documents, fetched once per page load. */
export function useLegal(): LegalInfo | null {
  const [info, setInfo] = useState<LegalInfo | null>(null);
  useEffect(() => {
    cache ??= api.legal();
    cache.then(setInfo).catch(() => { cache = null; });
  }, []);
  return info;
}

/* ------------------------------------------------------------ the text */

function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i}>{part.slice(1, -1)}</code>;
    return part;
  });
}

/** A document: blocks between blank lines; list items continue on indented lines. */
export function Doc({ markdown }: { markdown: string }) {
  const blocks = markdown.replace(/\r/g, '').split(/\n{2,}/);
  return (
    <div className="legal-doc">
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        const first = lines[0];
        if (/^---+$/.test(first.trim())) return <hr key={i} />;
        if (first.startsWith('# ')) return <h1 key={i}>{inline(first.slice(2))}</h1>;
        if (first.startsWith('## ')) return <h2 key={i}>{inline(first.slice(3))}</h2>;
        if (first.startsWith('### ')) return <h3 key={i}>{inline(first.slice(4))}</h3>;
        const numbered = /^\d+\.\s/.test(first), bulleted = /^-\s/.test(first);
        if (numbered || bulleted) {
          const items: { n: number; text: string }[] = [];
          for (const line of lines) {
            const m = numbered ? /^(\d+)\.\s+(.*)$/.exec(line) : /^-\s+(.*)$/.exec(line);
            if (m) items.push({ n: numbered ? Number(m[1]) : 0, text: numbered ? m[2] : m[1] });
            else if (items.length) items[items.length - 1].text += ` ${line.trim()}`;
          }
          const lis = items.map((it, k) => <li key={k} value={numbered ? it.n : undefined}>{inline(it.text)}</li>);
          return numbered ? <ol key={i}>{lis}</ol> : <ul key={i}>{lis}</ul>;
        }
        return <p key={i}>{inline(lines.join(' '))}</p>;
      })}
    </div>
  );
}

/** /terms, /disclaimer and /privacy: readable by anyone, signed in or not. */
export function LegalPage({ which }: { which: 'terms' | 'disclaimer' | 'privacy' }) {
  const info = useLegal();
  return (
    <main className="page narrow legal">
      <p><Link href="/" className="button back">← {t('appName')}</Link></p>
      {info ? <Doc markdown={info.documents[which]} /> : <p className="muted">{t('loading')}</p>}
    </main>
  );
}

/* ------------------------------------------------------- the first time */

/**
 * Before anything else: accept the current Terms and Disclaimer, or sign out.
 * The API records which version was accepted, and when (apps/api/src/legal.ts).
 */
export function TermsGate({ me, onAccepted }: { me: Me; onAccepted: () => void }) {
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  // the Service is for 13 and over (Terms s.3, Privacy Policy s.9): asked, not assumed
  const [oldEnough, setOldEnough] = useState(false);
  const updated = !!me.terms.acceptedVersion;
  const accept = async () => {
    setState({ busy: true });
    try {
      await api.acceptTerms(me.terms.version);
      onAccepted();
    } catch (err) {
      // the Terms changed while this screen was open: show the new version's screen
      setState({ error: (err as Error).message });
      onAccepted();
    }
  };
  const decline = () => signOutAndLeave();
  return (
    <AuthCard title={updated ? t('termsUpdatedTitle') : t('termsTitle')}>
      <p>{updated ? t('termsUpdatedBody', { date: me.terms.effectiveDate }) : t('termsBody')}</p>
      <ul className="terms-links">
        <li><a href="/terms" target="_blank" rel="noopener" className="button small">{t('termsLink')}</a></li>
        <li><a href="/disclaimer" target="_blank" rel="noopener" className="button small">{t('disclaimerLink')}</a></li>
        <li><a href="/privacy" target="_blank" rel="noopener" className="button small">{t('privacyLink')}</a></li>
      </ul>
      <label className="check"><input type="checkbox" checked={oldEnough} onChange={(e) => setOldEnough(e.target.checked)} />
        {t('ageConfirm')}</label>
      {state.error && <p className="error" role="alert">{state.error}</p>}
      <div className="row">
        <button className="primary" onClick={accept} disabled={state.busy || !oldEnough}>{t('termsAccept')}</button>
        <button onClick={decline} disabled={state.busy}>{t('termsDecline')}</button>
      </div>
    </AuthCard>
  );
}

/* ------------------------------------------------------------ contact */

/**
 * /contact: the Contact us form as a page, for a link from outside the app. Inside it, "Contact
 * us" opens the same form as a window (contact.tsx). Since 2026-11-01 no address of ours is
 * shown: the owner, "do not mention my email directly"; the server emails the message on.
 */
export function ContactPage() {
  return (
    <main className="page narrow contact">
      <p><Link href="/" className="button back">← {t('appName')}</Link></p>
      <h1>{t('contactTitle')}</h1>
      <ContactForm />
    </main>
  );
}

/* ---------------------------------------------------------- every page */

export function SiteFooter() {
  const info = useLegal();
  const owner = info?.legalName || t('appName');
  return (
    <footer className="site-footer no-print">
      <span>{t('footerCopyright', { year: new Date().getFullYear(), owner })}</span>
      <Link href="/welcome">{t('footerHome')}</Link>
      <Link href="/terms">{t('footerTerms')}</Link>
      <Link href="/disclaimer">{t('footerDisclaimer')}</Link>
      <Link href="/privacy">{t('footerPrivacy')}</Link>
      <ContactButton className="footer-link" label={t('footerContact')} />
    </footer>
  );
}
